import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PAGE_SIZE_OPTIONS, type LandingFilters, type SortBy } from '@/hooks/use-landing-filters';
import type { TemplateStatus } from '@/types/saved-template';
import type { Identifiable } from '@/types/template';

import { SitesMultiSelect } from './sites-multi-select';

/**
 * Site, status, sort and page-size bar above the templates table (Task 6.8).
 * Read-through to URL-driven filter state via `setFilter` from
 * `useLandingFilters`; this component is pure presentation.
 *
 * Status options are `published | draft | deleted`. Deleted is a reversible
 * soft delete; deleted templates are listed only when the Deleted filter is
 * active, and can be restored. Sort options match `SortBy` from the hook.
 */
export function TemplatesFilters({ filters, setFilter, sites }: TemplatesFiltersProps) {
  const { t } = useLingui();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 rotaready:flex-nowrap rotaready:items-center rotaready:gap-[10px]">
      <div className="flex flex-wrap items-end gap-3 rotaready:flex-nowrap rotaready:items-center rotaready:gap-[10px]">
        <SitesMultiSelect
          sites={sites}
          value={filters.sites}
          onChange={(ids) => setFilter('sites', ids)}
        />
        <ButtonGroup aria-label={t`Filter by status`} className="rotaready:[&>*]:!rounded-[4px]">
          {STATUS_OPTION_IDS.map((id) => {
            const active = filters.statuses.includes(id);
            // The last remaining active status can't be switched off — an empty
            // status set falls back to DEFAULT_STATUSES and visually "resets" the
            // filter. Reflect that here so the disabled state is obvious to users;
            // "Clear all filters" is the way to reset.
            const isLastActive = active && filters.statuses.length === 1;
            return (
              <Button
                key={id}
                type="button"
                variant={active ? 'secondary' : 'outline'}
                size="default"
                aria-pressed={active}
                aria-disabled={isLastActive}
                className={cn(
                  'rotaready:h-[30px] rotaready:px-3 rotaready:py-0 rotaready:text-base rotaready:border-transparent',
                  active
                    ? 'rotaready:bg-[#475b68] rotaready:text-white'
                    : 'rotaready:bg-transparent rotaready:text-[#475b68] rotaready:hover:bg-[#475b68]/10 rotaready:hover:text-[#475b68]',
                )}
                onClick={() =>
                  toggleStatus(filters.statuses, id, (next) => setFilter('statuses', next))
                }
              >
                {renderStatusLabel(id)}
              </Button>
            );
          })}
        </ButtonGroup>
      </div>
      <div className="flex flex-wrap items-end gap-3 rotaready:flex-nowrap rotaready:items-center rotaready:gap-[10px]">
        <Select value={filters.sortBy} onValueChange={(v) => setFilter('sortBy', v as SortBy)}>
          <SelectTrigger
            aria-label={t`Sort templates`}
            className="w-48 rotaready:!h-[30px] rotaready:py-0 rotaready:rounded-[4px] rotaready:border-transparent rotaready:bg-[#475b68] rotaready:text-base rotaready:font-[var(--button-font-weight)] rotaready:text-white rotaready:[&_svg]:text-white rotaready:[&_svg]:opacity-100"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTION_IDS.map((id) => (
              <SelectItem key={id} value={id}>
                {renderSortLabel(id)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={String(filters.pageSize)}
          onValueChange={(v) => setFilter('pageSize', Number(v))}
        >
          <SelectTrigger
            aria-label={t`Templates per page`}
            className="w-36 rotaready:!h-[30px] rotaready:py-0 rotaready:rounded-[4px] rotaready:border-transparent rotaready:bg-[#475b68] rotaready:text-base rotaready:font-[var(--button-font-weight)] rotaready:text-white rotaready:[&_svg]:text-white rotaready:[&_svg]:opacity-100"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                <Trans>{size} per page</Trans>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/**
 * Toggles `status` in or out of the active status filter and forwards
 * the new array to `setStatuses` (which the parent routes through
 * `useLandingFilters.setFilter('statuses', …)`).
 */
function toggleStatus(
  current: TemplateStatus[],
  status: TemplateStatus,
  setStatuses: (next: TemplateStatus[]) => void,
) {
  const isActive = current.includes(status);
  // Never switch off the last active filter — an empty status set falls back to
  // DEFAULT_STATUSES in `useLandingFilters` and visually "resets" the filter.
  if (isActive && current.length === 1) return;
  setStatuses(isActive ? current.filter((x) => x !== status) : [...current, status]);
}

/** Status pill labels — `<Trans>` per case so the extractor picks them up at build time. */
function renderStatusLabel(id: (typeof STATUS_OPTION_IDS)[number]): ReactNode {
  switch (id) {
    case 'published':
      return <Trans>Published</Trans>;
    case 'draft':
      return <Trans>Draft</Trans>;
    case 'deleted':
      return <Trans>Deleted</Trans>;
  }
}

/** Sort dropdown labels — same pattern as `renderStatusLabel`. */
function renderSortLabel(id: SortBy): ReactNode {
  switch (id) {
    case 'recentlyModified':
      return <Trans>Recently modified</Trans>;
    case 'alphabetical':
      return <Trans>Alphabetical (A-Z)</Trans>;
    case 'recentlyCreated':
      return <Trans>Recently created</Trans>;
    case 'lastUsed':
      return <Trans>Last used</Trans>;
  }
}

const STATUS_OPTION_IDS = [
  'published',
  'draft',
  'deleted',
] as const satisfies readonly TemplateStatus[];

const SORT_OPTION_IDS = [
  'recentlyModified',
  'alphabetical',
  'recentlyCreated',
  'lastUsed',
] as const satisfies readonly SortBy[];

interface TemplatesFiltersProps {
  filters: LandingFilters;
  setFilter: <K extends keyof LandingFilters>(key: K, value: LandingFilters[K]) => void;
  sites: Identifiable[];
}
